const UPDATE_INTERVAL_MS = 60 * 60 * 1_000;
const VERSION_TIMEOUT_MS = 2_000;

export const registerOfflineSupport = (): void => {
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
  window.addEventListener("load", () => {
    void new OfflineUpdater().start().catch(() => {
      // The game still works online when installation is blocked by the browser.
    });
  });
};

export const getOfflineVersion = (): Promise<string | null> =>
  requestWorkerVersion(navigator.serviceWorker?.controller ?? null);

class OfflineUpdater {
  private registration: ServiceWorkerRegistration | null = null;

  async start(): Promise<void> {
    this.registration = await navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });
    this.watchRegistration(this.registration);
    await this.checkForUpdate();
    await this.publishActiveVersion();
    window.addEventListener("online", () => void this.checkForUpdate());
    window.setInterval(() => void this.checkForUpdate(), UPDATE_INTERVAL_MS);
    navigator.serviceWorker.addEventListener("controllerchange", () => void this.publishActiveVersion());
  }

  private watchRegistration(registration: ServiceWorkerRegistration): void {
    registration.addEventListener("updatefound", () => {
      const worker = registration.installing;
      worker?.addEventListener("statechange", () => {
        if (worker.state === "installed" && registration.waiting) void this.publishPendingVersion(registration.waiting);
      });
    });
    if (registration.waiting) void this.publishPendingVersion(registration.waiting);
  }

  private async checkForUpdate(): Promise<void> {
    try {
      await this.registration?.update();
    } catch {
      // Offline play keeps using the last complete cache.
    }
  }

  private async publishActiveVersion(): Promise<void> {
    const version = await getOfflineVersion();
    if (version) document.documentElement.dataset.offlineVersion = version;
  }

  private async publishPendingVersion(worker: ServiceWorker): Promise<void> {
    const version = await requestWorkerVersion(worker);
    if (version) document.documentElement.dataset.offlineUpdate = version;
  }
}

const requestWorkerVersion = (worker: ServiceWorker | null): Promise<string | null> => {
  if (!worker) return Promise.resolve(null);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    let settled = false;
    const finish = (version: string | null): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      channel.port1.close();
      resolve(version);
    };
    const timer = window.setTimeout(() => finish(null), VERSION_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent<{ version?: unknown }>) => {
      finish(typeof event.data?.version === "string" ? event.data.version : null);
    };
    worker.postMessage({ type: "GET_VERSION" }, [channel.port2]);
  });
};
